import { memo } from "react";

import { styled } from "@mui/material/styles";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";

/**
 * Props for Linear Progress Component.
 * @interface
 * @name LinearProgressComponentProps
 * @kind interface
 */
interface LinearProgressComponentProps {
  /**
   * Primary color of the progress indicator.
   * @property
   * @name primaryColor
   * @kind property
   * @memberof LinearProgressComponentProps
   * @type {string}
   */
  primaryColor: string;
  /**
   * Secondary color of the progress indicator.
   * @property
   * @name secondaryColor
   * @kind property
   * @memberof LinearProgressComponentProps
   * @type {string}
   */
  secondaryColor: string;
  /**
   * The value of the progress indicator
   * for the determinate and buffer variants.
   * Value between 0 and 100.
   * @default 0
   * @property
   * @name value
   * @kind property
   * @memberof LinearProgressComponentProps
   * @type {number | undefined}
   */
  value: number | undefined;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof LinearProgressComponentProps
   * @type {string | undefined}
   */
  className?: string | undefined;
}

/**
 * Memoized Linear Progress Component.
 * @constant
 * @name LinearProgressComponent
 * @kind variable
 * @type {React.NamedExoticComponent<LinearProgressComponentProps>}
 */
const LinearProgressComponent: React.NamedExoticComponent<LinearProgressComponentProps> =
  memo(
    /**
     * Linear Progress Component.
     * @function
     * @name LinearProgressComponent
     * @kind function
     * @memberof LinearProgressComponent
     * @param {LinearProgressComponentProps} props Props for Linear Progress Component.
     * @returns {JSX.Element} Linear Progress Component.
     */
    function LinearProgressComponent(
      props: LinearProgressComponentProps
    ): JSX.Element {
      const BorderedLinearProgress = styled(LinearProgress)(() => ({
        borderRadius: 5,
        [`&.${linearProgressClasses.colorPrimary}`]: {
          backgroundColor: props.primaryColor,
        },
        [`& .${linearProgressClasses.bar}`]: {
          borderRadius: 5,
          backgroundColor: props.secondaryColor,
        },
      }));

      return (
        <BorderedLinearProgress
          className={props.className}
          variant="determinate"
          value={props.value}
        />
      );
    }
  );

export default LinearProgressComponent;
